/**
 * useBrokerGate
 *
 * Hook that manages whether to show the BrokerTokenGateModal after login.
 *
 * Logic:
 *   - Runs once after the user's JWT session is confirmed (isAuthenticated=true)
 *   - Fetches GET /broker/current to check token_status
 *   - Shows the gate modal if token_status is EXPIRED or NOT_SET
 *   - Does NOT show the modal if the user already dismissed it this session
 *     (stored in sessionStorage so it resets on browser close/new tab)
 *   - Shows a "Connect a Broker" prompt if no broker is connected (never for
 *     Operators, for whom a personal broker is optional)
 *   - End users see either prompt only with a loaded, active, unexpired, non-TRIAL
 *     subscription (broker mapping is a paid feature); loading or failed
 *     subscription data never shows it. Operators get the session prompt for a
 *     mapped broker without needing a personal subscription.
 *
 * Usage — in your top-level layout component (e.g. AppLayout.jsx):
 *
 *   import { useBrokerGate }       from './pages/broker/useBrokerGate';
 *   import { BrokerTokenGateModal } from './pages/broker/BrokerTokenGateModal';
 *
 *   function AppLayout({ children }) {
 *     const { showGate, dismissGate, onGateSuccess } = useBrokerGate({ isAuthenticated });
 *
 *     return (
 *       <>
 *         {children}
 *         <BrokerTokenGateModal
 *           open={showGate}
 *           onSuccess={onGateSuccess}
 *           onDismiss={dismissGate}
 *         />
 *       </>
 *     );
 *   }
 *
 * MIGRATION NOTE:
 *   Replace the `isAuthenticated` check with your actual auth state.
 *   In ALGOSYS standalone-token mode, use the Zustand session store:
 *     const isAuthenticated = useSessionStore((s) => s.status === 'authenticated');
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { brokerApi } from '../../api/brokerClient';
import { useMySubscription } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { computeBrokerGate, isAutomaticBrokerReminderEligible } from './brokerGateVisibility';

const SESSION_DISMISSED_KEY = 'broker_gate_dismissed';
const SESSION_MAP_DISMISSED_KEY = 'broker_map_dismissed';

export function useBrokerGate({ isAuthenticated = false } = {}) {
  const queryClient = useQueryClient();
  const mustChangePassword = useSessionStore((s) => s.mustChangePassword);
  const role = useSessionStore((s) => s.role);
  const currentUserId = useSessionStore((s) => s.user?.id ?? null);
  const skipGate = !isAuthenticated || mustChangePassword || role === 'admin';

  // Same per-user query the header already uses, so no extra request.
  const subscriptionQuery = useMySubscription();
  const reminderEligible = !skipGate && isAutomaticBrokerReminderEligible({
    role,
    subscription: subscriptionQuery.data,
    subscriptionLoaded: subscriptionQuery.isSuccess,
    userId: currentUserId,
  });

  const { data: status } = useQuery({
    queryKey: ['brokerStatus'],
    queryFn:  brokerApi.getCurrent,
    enabled:  !skipGate,
    staleTime: 5 * 60 * 1000,
  });

  // Dismissal state mirrors sessionStorage. Held in React state so a click
  // triggers a re-render in the same cycle — guaranteeing the gate closes.
  const [tokenDismissed, setTokenDismissed] = useState(
    () => !!sessionStorage.getItem(SESSION_DISMISSED_KEY)
  );
  const [mapDismissed, setMapDismissed] = useState(
    () => !!sessionStorage.getItem(SESSION_MAP_DISMISSED_KEY)
  );

  // Pure derivation from status + dismissal + route — no useEffect race.
  // Excludes the Broker Settings page (/broker) so the gate never covers the
  // page it directs users to; it re-appears if they leave while still invalid.
  const location = useLocation();
  const { showGate, gateReason } = computeBrokerGate({
    skipGate,
    reminderEligible,
    role,
    status,
    tokenDismissed,
    mapDismissed,
    pathname: location.pathname,
  });

  function dismissGate() {
    if (gateReason === 'no_broker') {
      sessionStorage.setItem(SESSION_MAP_DISMISSED_KEY, '1');
      setMapDismissed(true);
    } else {
      sessionStorage.setItem(SESSION_DISMISSED_KEY, '1');
      setTokenDismissed(true);
    }
  }

  function onGateSuccess() {
    queryClient.invalidateQueries({ queryKey: ['brokerFunds'] });
    queryClient.invalidateQueries({ queryKey: ['brokerStatus'] });
    queryClient.invalidateQueries({ queryKey: ['brokerCurrent'] });
    // Clear dismissals so the gate naturally hides via fresh status, and
    // re-shows immediately if the next status refresh still finds a problem.
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    sessionStorage.removeItem(SESSION_MAP_DISMISSED_KEY);
    setTokenDismissed(false);
    setMapDismissed(false);
  }

  // After OAuth redirect returns — refetch status; re-show gate if still invalid.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') !== 'connected') return;
    window.history.replaceState({}, '', window.location.pathname);
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    setTokenDismissed(false);
    queryClient.invalidateQueries({ queryKey: ['brokerStatus'] });
    queryClient.invalidateQueries({ queryKey: ['brokerFunds'] });
  }, [queryClient]);

  return { showGate, gateReason, dismissGate, onGateSuccess };
}
